import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "search", "results", "searchStep", "detailStep", "formStep", "pointSummary",
    "name", "pickpostStreet", "pickpostZip", "pickpostCity", "street", "postalCode", "city",
    "notify", "email", "sms", "emailGroup", "smsGroup", "mobile"
  ]
  static values = { pointsUrl: String, searchTitle: String, detailTitle: String }

  connect() {
    this.element.dataset.pickpostReady = "true"
    this.searchTarget.focus()
    this.searchTarget.select()
    this.updateNotifications()
    if (this.searchTarget.value.trim().length >= 2) this.search()
  }

  disconnect() {
    window.clearTimeout(this.searchTimeout)
    this.request?.abort()
    delete this.element.dataset.pickpostReady
  }

  queueSearch() {
    window.clearTimeout(this.searchTimeout)
    this.request?.abort()

    if (this.searchTarget.value.trim().length < 2) {
      this.resultsTarget.replaceChildren()
      return
    }

    this.searchTimeout = window.setTimeout(() => this.search(), 200)
  }

  async search() {
    const url = new URL(this.pointsUrlValue, window.location.origin)
    url.searchParams.set("search", this.searchTarget.value.trim())
    const request = new AbortController()
    this.request = request

    try {
      const response = await fetch(url, { headers: { Accept: "text/html" }, signal: request.signal })
      if (!response.ok) throw new Error(`PickPost search failed with status ${response.status}`)
      this.resultsTarget.innerHTML = await response.text()
    } catch (error) {
      if (error.name !== "AbortError") this.resultsTarget.replaceChildren()
    } finally {
      if (this.request === request) this.request = null
    }
  }

  async selectPoint(event) {
    this.request?.abort()
    const request = new AbortController()
    this.request = request

    try {
      const response = await fetch(event.currentTarget.dataset.url, {
        headers: { Accept: "text/html" }, signal: request.signal
      })
      if (!response.ok) throw new Error(`PickPost point failed with status ${response.status}`)

      this.detailStepTarget.innerHTML = await response.text()
      this.applyPoint(this.detailStepTarget.querySelector("[data-pickpost-point]"))
      this.showStep(this.detailStepTarget)
      this.setModalTitle(this.detailTitleValue, true)
    } catch (error) {
      if (error.name !== "AbortError") this.detailStepTarget.replaceChildren()
    } finally {
      if (this.request === request) this.request = null
    }
  }

  showSearch() {
    this.showStep(this.searchStepTarget)
    this.setModalTitle(this.searchTitleValue, false)
  }

  confirmPoint() {
    this.showStep(this.formStepTarget)
  }

  updateNotifications() {
    this.emailGroupTarget.hidden = !this.emailTarget.checked
    this.smsGroupTarget.hidden = !this.smsTarget.checked
    this.mobileTarget.required = this.smsTarget.checked

    const notifications = []
    if (this.smsTarget.checked) notifications.push("SMS")
    if (this.emailTarget.checked) notifications.push("EMAIL")
    this.notifyTarget.value = notifications.join("_")
    if (notifications.length) this.notifyTarget.closest("#notify")?.classList.remove("error")
  }

  validate(event) {
    this.updateNotifications()
    if (this.notifyTarget.value) return

    event.preventDefault()
    event.stopImmediatePropagation()
    this.notifyTarget.closest("#notify")?.classList.add("error")
  }

  applyPoint(point) {
    if (!point) return

    this.pointSummaryTarget.innerHTML = point.querySelector("[data-pickpost-address]").innerHTML
    this.nameTarget.value = point.dataset.name
    this.pickpostStreetTarget.value = point.dataset.deliveryStreet
    this.pickpostZipTarget.value = point.dataset.deliveryZip
    this.pickpostCityTarget.value = point.dataset.deliveryCity
    this.streetTarget.value = point.dataset.street
    this.postalCodeTarget.value = point.dataset.zip
    this.cityTarget.value = point.dataset.city
  }

  showStep(step) {
    this.element.querySelectorAll(".step").forEach(element => element.hidden = element !== step)
  }

  setModalTitle(title, back) {
    const titleElement = this.element.closest(".modal")?.querySelector(".modal-header .title")
    if (!titleElement) return

    titleElement.textContent = title
    titleElement.style.cursor = back ? "pointer" : "auto"
    titleElement.onclick = back ? () => this.showSearch() : null
  }
}
